import { useEffect, useState } from "react";
import { Phone, Loader2 } from "lucide-react";
import { fetchPricing } from "../lib/api";
import { BUSINESS } from "../config/business";
import SEO from "../components/SEO";
import { FadeUp, MaskedLine } from "../components/anim";

export default function Pricing() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetchPricing().then(setItems).catch(() => setItems([]));
  }, []);

  const priceSchema = items && items.length ? {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "Smog Check Pricing Modesto",
    itemListElement: items.filter((i) => /^\d/.test(i.sale_price || i.regular_price || "")).map((i) => ({
      "@type": "Offer",
      name: i.service_name,
      price: (i.sale_price || i.regular_price),
      priceCurrency: "USD",
    })),
  } : null;

  const categories = items ? [...new Set(items.map((i) => i.category))] : [];

  const fmt = (v) => (v && /^\d/.test(v) ? `$${v}` : v || "Call");

  return (
    <div data-testid="pricing-page">
      <SEO
        title={`Smog Check Prices Modesto, CA | ${BUSINESS.name}`}
        description="Clear, honest smog check pricing in Modesto — no upcharges for SUVs, trucks or STAR-certified vehicles. Mention our $20 off coupon."
        keywords="smog check price Modesto, smog cost Modesto CA, STAR smog price"
        path="/pricing"
        schema={priceSchema ? [priceSchema] : []}
      />
      <section className="bg-white aurora relative overflow-hidden">
        <div className="section relative z-10 py-14">
          <p className="kicker">Pricing</p>
          <h1 className="mt-2 font-head font-extrabold text-4xl sm:text-5xl text-ink">
            <MaskedLine lines={[{ text: "Clear Smog Check Pricing" }]} />
          </h1>
          <p className="mt-4 text-ink/70 font-body max-w-2xl">We won't nickel-and-dime you for STAR certification, SUVs, pickups or vans. Mention our coupon to save even more.</p>
        </div>
      </section>

      <section className="section py-14">
        {items === null && (
          <div className="flex items-center justify-center py-20 text-volt"><Loader2 className="h-8 w-8 animate-spin" /></div>
        )}
        {items && items.length === 0 && (
          <div className="text-center py-20 text-ink/60 font-body">Pricing is being updated. Please call {BUSINESS.phoneDisplay} for current rates.</div>
        )}
        <div className="grid md:grid-cols-2 gap-8">
          {categories.map((cat) => (
            <FadeUp key={cat}>
              <div className="rounded-2xl overflow-hidden border border-line shadow-sm bg-white">
                <div className="bg-deep text-white px-6 py-4 font-head font-bold text-lg">{cat}</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-ink/50 font-body border-b border-line">
                      <th className="px-6 py-3 font-semibold">Service</th>
                      <th className="px-4 py-3 font-semibold">Price</th>
                      <th className="px-6 py-3 font-semibold">With Coupon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.filter((i) => i.category === cat).map((i) => (
                      <tr key={i.id} className="border-b border-line/60 last:border-0" data-testid={`price-row-${i.id}`}>
                        <td className="px-6 py-4">
                          <p className="font-head font-semibold text-ink">{i.service_name}</p>
                          {i.description && <p className="text-xs text-ink/55 mt-0.5">{i.description}</p>}
                        </td>
                        <td className="px-4 py-4 font-head font-bold text-ink whitespace-nowrap">{fmt(i.regular_price)}</td>
                        <td className="px-6 py-4 font-head font-bold text-openg whitespace-nowrap">{fmt(i.sale_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a href={`tel:${BUSINESS.phoneTel}`} className="btn-cta"><Phone className="h-4 w-4" /> Get a Quote — {BUSINESS.phoneDisplay}</a>
        </div>
      </section>
    </div>
  );
}
